"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, LocateIcon } from "lucide-react";
import { Banner } from "../banner";
import { useForm, ValidationError } from '@formspree/react';
import { Navigate, useNavigate } from "react-router-dom";


const contactInfo = [
  {
    icon: Phone,
    title: "Contact Numbers",
    items: ["+971 56 166 8746"],
    iconBg: "bg-blue-50",
    iconColor: "text-blue-500",
  },
  {
    icon: Mail,
    title: "Email Address",
    items: ["info@interpetro-chem.com"],
    iconBg: "bg-orange-50",
    iconColor: "text-orange-500",
  },
  {
    icon: LocateIcon,
    title: "Address",
    items: ["Interpetro Chemicals Trading FZ-LLC || PO. BOX 81923, SBAM0033 Compass Building, Al Shohada Road, AL Hamra Industrial Zone-FZ, Ras Al Khaimah, United Arab Emirates. "
    ],
    iconBg: "bg-green-50",
    iconColor: "text-green-500",
  },
];



export function ContactForm() {
  const [state, handleSubmit] = useForm("mkgjvwep");
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate(); // Using React Router's navigation

  const handleFormSubmit = async (e) => {
    e.preventDefault(); // Prevent default behavior

    const result = await handleSubmit(e); // Submitting the form

    if (result?.succeeded) {
      setSubmitted(true); // Show success message
      e.target.reset(); // Reset form fields
      setTimeout(() => navigate("/"), 2000); // Navigate after 2s
    }
  };

  return (
    <>
      <Banner page={'Contact'} heading={'Contact with us'} image={'/images/contact-us.jpg'} />

      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white p-6 rounded-lg shadow-lg"
                  >
                    <div className={`p-4 rounded-full inline-block ${item.iconBg}`}>
                      <Icon className={`w-8 h-8 ${item.iconColor}`} />
                    </div>
                    <h3 className="font-medium mb-3">{item.title}</h3>
                    {item.items.map((text, i) => (
                      <p key={i} className="text-gray-600 text-sm break-words">
                        {item.title === "Email Address" ? (
                          <a href={`mailto:${text}`} className="text-gray-600 hover:underline">
                            {text}
                          </a>
                        ) : item.title === "Contact Numbers" ? (
                          <a href={`tel:${text}`} className="text-gray-600 hover:underline">
                            {text}
                          </a>
                        ) : (
                          text
                        )}
                      </p>

                    ))}
                  </motion.div>
                );
              })}
            </div>

            <div className="flex items-center gap-6 flex-col-reverse lg:flex-row">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="bg-white p-8 rounded-lg lg:w-2/4 w-full shadow-xl"
              >
                {submitted ? (
                  <h2 className="text-2xl font-bold mb-6 text-green-600 ">
                    Thanks for contacting us!
                  </h2>
                ) : (<h2 className="text-2xl font-bold mb-6">Get in Touch with Us</h2>
                )}
                <form onSubmit={handleFormSubmit} className="bg-white p-6 rounded-lg shadow-md w-full max-w-lg mx-auto space-y-4">
                  <div className="flex flex-col">
                    <label htmlFor="email" className="text-gray-700 font-medium mb-1">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <ValidationError prefix="Email" field="email" errors={state.errors} className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="flex flex-col">
                    <label htmlFor="message" className="text-gray-700 font-medium mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      required
                    />
                    <ValidationError prefix="Message" field="message" errors={state.errors} className="text-red-500 text-sm mt-1" />
                  </div>

                  <button
                    type="submit"
                    disabled={state.submitting}
                    className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-8 rounded-lg focus:outline-none"
                  >
                    {state.submitting ? "Submitting..." : "Submit"}
                  </button>
                </form>

              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                className="h-full w-full overflow-hidden lg:w-2/4 shadow-lg"
              >
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24692.11469078886!2d55.95128752207339!3d25.52142615424978!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5c59c49b6d7a5%3A0xfbe4d0272d2e9c11!2sAl%20Shuhada&#39;%20Rd%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2s!4v1733114960500!5m2!1sen!2s" width="600" height="480" allowFullScreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="locate"></iframe>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
